(function () {
  'use strict';

  var DEFAULT_LIMIT = 10;
  var DASH_REGEX = /[\u2013\u2014]/g;
  var WHITESPACE_REGEX = /\s+/g;

  /**
   * Normalize a reference string for exact comparison.
   *
   * - decodes HTML entities
   * - strips HTML tags and takes the first paragraph
   * - normalizes unicode
   * - collapses whitespace
   * - trims
   * - lowercases for comparison
   * - normalizes en/em dashes to "-"
   *
   * Returns "" when input is empty or unparseable.
   */
  function normalizeReference(raw) {
    if (typeof raw !== 'string') return '';
    var text = raw;
    // Decode the entities most commonly seen in Smile reward descriptions.
    var entities = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
      '&nbsp;': ' ',
      '&mdash;': '-',
      '&ndash;': '-',
    };
    text = text.replace(/&(?:amp|lt|gt|quot|apos|#39|nbsp|mdash|ndash);/g, function (match) {
      return entities[match] != null ? entities[match] : match;
    });

    // Strip HTML tags and split into paragraphs.
    var tagOnly = /<\s*\/?\s*(?:p|br|li|h[1-6])\s*[^>]*>/gi;
    var parts = String(text).split(tagOnly);
    var firstParagraph = '';
    for (var partIndex = 0; partIndex < parts.length; partIndex += 1) {
      var part = parts[partIndex].replace(/<[^>]+>/g, ' ').trim();
      if (part) {
        firstParagraph = part;
        break;
      }
    }
    var paragraph = firstParagraph || text.replace(/<[^>]+>/g, ' ').trim();
    if (!paragraph) return '';

    // Unicode normalize then lowercase + collapse whitespace + dash unify.
    paragraph = paragraph.normalize ? paragraph.normalize('NFKC') : paragraph;
    paragraph = paragraph.replace(DASH_REGEX, '-');
    paragraph = paragraph.replace(WHITESPACE_REGEX, ' ').trim();
    if (!paragraph) return '';

    return paragraph.toLowerCase();
  }

  /**
   * Strip the Smile-name decorations used as a fallback when description is empty.
   * - leading "Free "
   * - trailing "| No Combined Discounts ..." or similar trailing notes
   *
   * Does NOT remove ordinary words, digits, model numbers or dashes inside the
   * product/variant title.
   */
  function cleanRewardName(name) {
    if (typeof name !== 'string') return '';
    var cleaned = name;
    cleaned = cleaned.replace(/^Free\s+/i, '');
    cleaned = cleaned.replace(/\s*\|\s*No Combined Discounts.*$/i, '');
    return cleaned.replace(WHITESPACE_REGEX, ' ').trim();
  }

  /**
   * Convert a reward name to a Shopify product handle slug.
   *
   * Used as a fallback path when the description-based search returns 0
   * candidates (e.g. Unlisted products that are excluded from the predictive
   * search index). The /products/<handle>.js endpoint still serves Unlisted
   * product JSON, so a slug direct call can recover the real product image
   * for these cases.
   *
   * Returns "" when input is empty or the cleaned slug is empty.
   */
  function nameToSlug(name) {
    if (typeof name !== 'string') return '';
    var cleaned = cleanRewardName(name);
    if (!cleaned) return '';
    var slug = cleaned.toLowerCase();
    slug = slug.replace(/[^a-z0-9]+/g, '-');
    slug = slug.replace(/^-+|-+$/g, '');
    return slug;
  }

  function extractReferenceFromRewardDescription(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return '';
    var text = raw;
    var entities = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
      '&nbsp;': ' ',
      '&mdash;': '-',
      '&ndash;': '-',
    };
    text = text.replace(/&(?:amp|lt|gt|quot|apos|#39|nbsp|mdash|ndash);/g, function (match) {
      return entities[match] != null ? entities[match] : match;
    });
    text = text.replace(/<[^>]+>/g, ' ');
    text = text.normalize ? text.normalize('NFKC') : text;
    text = text.replace(DASH_REGEX, '-').replace(WHITESPACE_REGEX, ' ').trim();
    if (!text) return '';

    var match = text.match(/reward can only be used on\s+(.+?)(?:\.\s|\.$)/i);
    if (match && match[1]) {
      return normalizeReference(match[1]);
    }

    return '';
  }

  /**
   * Pick the best reference text in priority order:
   *   1) structured phrase extracted from reward.description
   *   2) normalized reward.description first paragraph
   *   3) cleaned reward.name
   *   4) "" (no usable text)
   */
  function pickReference(input) {
    var extractedDescription = extractReferenceFromRewardDescription(input && input.description);
    if (extractedDescription) return extractedDescription;
    var description = normalizeReference(input && input.description);
    if (description) return description;
    var name = cleanRewardName(input && input.name);
    var normalizedName = normalizeReference(name);
    if (normalizedName) return normalizedName;
    return '';
  }

  /**
   * Build a basic Search/Product Adapter backed by Shopify Predictive Search +
   * Shopify Product Ajax JSON. Kept outside `resolve()` so tests can inject
   * an in-memory adapter.
   */
  function createShopifyCatalogAdapter(options) {
    options = options || {};
    var fetchImpl = options.fetch || (typeof fetch === 'function' ? fetch : null);
    var root = (options.root != null ? options.root : '/') || '/';
    if (typeof root !== 'string' || root.charAt(0) !== '/') {
      root = '/' + root;
    }
    if (root.charAt(root.length - 1) !== '/') {
      root = root + '/';
    }

    function delay(ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    }

    function fetchJsonWithRetry(url, attemptsLeft) {
      if (!fetchImpl) return Promise.reject(new Error('fetch is unavailable'));
      var remaining = typeof attemptsLeft === 'number' ? attemptsLeft : 3;
      return fetchImpl(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response || !response.ok) {
            var status = response && response.status;
            if (remaining > 1 && (status === 429 || status >= 500)) {
              return delay(300).then(function () {
                return fetchJsonWithRetry(url, remaining - 1);
              });
            }
            throw new Error('Shopify JSON fetch failed with status ' + status + '.');
          }
          return response.json();
        })
        .catch(function (error) {
          if (remaining > 1) {
            return delay(300).then(function () {
              return fetchJsonWithRetry(url, remaining - 1);
            });
          }
          throw error;
        });
    }

    function buildSearchUrl(query, limit) {
      var params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(limit));
      params.set('resources[options][unavailable_products]', 'show');
      return root + 'search/suggest.json?' + params.toString();
    }

    function searchProducts(query, limit) {
      var safeLimit = typeof limit === 'number' && limit > 0 ? limit : DEFAULT_LIMIT;
      var url = buildSearchUrl(query, safeLimit);
      return fetchJsonWithRetry(url, 3)
        .then(function (payload) {
          var products = payload && payload.resources && payload.resources.results && payload.resources.results.products;
          if (!Array.isArray(products)) return [];
          return products.map(function (entry) {
            return {
              handle: entry && (entry.handle || (entry.url ? entry.url.split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '') : '')) || '',
              title: entry && entry.title ? String(entry.title) : '',
              featuredImage: pickImage(entry),
              variants: Array.isArray(entry && entry.variants)
                ? entry.variants.map(function (variant) {
                    return {
                      id: variant && variant.id != null ? String(variant.id) : '',
                      title: variant && variant.title ? String(variant.title) : '',
                      image: pickVariantImage(variant),
                    };
                  })
                : [],
            };
          }).filter(function (entry) {
            return entry.handle;
          });
        });
    }

    function loadProduct(handle) {
      if (!handle) return Promise.reject(new Error('Product handle is empty'));
      var url = root + 'products/' + handle + '.js';
      return fetchJsonWithRetry(url, 3)
        .then(function (product) {
          if (!product || typeof product !== 'object') {
            throw new Error('Shopify product JSON is empty for handle ' + handle);
          }
          return {
            handle: product.handle || handle,
            title: product.title || '',
            featuredImage: product.featured_image || pickImage(product) || '',
            featuredImageAlt: product.featured_image_alt || '',
            variants: Array.isArray(product.variants)
              ? product.variants.map(function (variant) {
                  return {
                    id: variant && variant.id != null ? String(variant.id) : '',
                    title: variant && variant.title ? String(variant.title) : '',
                    image: pickVariantImage(variant) || '',
                    featuredImage: pickVariantImage(variant) || '',
                  };
                })
              : [],
          };
        });
    }

    return { searchProducts: searchProducts, loadProduct: loadProduct };
  }

  function pickImage(entry) {
    if (!entry) return '';
    if (entry.featured_image && entry.featured_image.url) return String(entry.featured_image.url);
    if (entry.image) return String(entry.image);
    return '';
  }

  function pickVariantImage(variant) {
    if (!variant) return '';
    if (variant.featured_image) {
      if (typeof variant.featured_image === 'string') return variant.featured_image;
      if (variant.featured_image.url) return String(variant.featured_image.url);
      if (variant.featured_image.src) return String(variant.featured_image.src);
    }
    if (variant.featured_media && variant.featured_media.preview_image) {
      if (variant.featured_media.preview_image.url) return String(variant.featured_media.preview_image.url);
      if (variant.featured_media.preview_image.src) return String(variant.featured_media.preview_image.src);
    }
    if (variant.image) {
      if (typeof variant.image === 'string') return variant.image;
      if (variant.image.url) return String(variant.image.url);
      if (variant.image.src) return String(variant.image.src);
    }
    return '';
  }

  /**
   * Detect whether `remainder` directly follows a complete product title as a
   * variant suffix. Accepts " - ", " – ", " — ", " " and empty as separators.
   */
  function stripProductPrefix(reference, productTitle) {
    if (reference === productTitle) return '';
    var separators = [' - ', ' \u2013 ', ' \u2014 ', ' '];
    for (var index = 0; index < separators.length; index += 1) {
      var separator = separators[index];
      if (reference.length > productTitle.length + separator.length) {
        var prefix = reference.substr(0, productTitle.length);
        var tail = reference.substr(productTitle.length);
        if (prefix === productTitle && tail.indexOf(separator) === 0) {
          return tail.substr(separator.length);
        }
      }
    }
    return null;
  }

  /**
   * Decide which image a reward should display.
   *
   * Returns:
   *   { imageUrl, matchLevel, productHandle, variantId, reason }
   *
   * matchLevel:
   *   'variant'  -> unique variant match (variant image preferred, falls back to product)
   *   'product'  -> unique product match
   *   'fallback' -> no unique match, returns Smile image
   */
  function selectImage(product, remainder, smileImage) {
    var productImage = product.featuredImage || '';
    var productHandle = product.handle || '';
    var variantMatches = [];
    var firstVariantMatchIndex = -1;

    if (remainder !== null) {
      for (var index = 0; index < product.variants.length; index += 1) {
        var variant = product.variants[index];
        var variantTitle = normalizeReference(variant.title);
        if (variantTitle && variantTitle === remainder) {
          variantMatches.push({ index: index, variant: variant });
          if (firstVariantMatchIndex === -1) firstVariantMatchIndex = index;
        }
      }
    }

    if (remainder !== null && variantMatches.length === 1) {
      var matchedVariant = variantMatches[0].variant;
      if (matchedVariant.image || matchedVariant.featuredImage) {
        return {
          imageUrl: matchedVariant.image || matchedVariant.featuredImage,
          matchLevel: 'variant',
          productHandle: productHandle,
          variantId: matchedVariant.id || '',
          reason: 'variant-match',
        };
      }
      return {
        imageUrl: productImage,
        matchLevel: 'variant',
        productHandle: productHandle,
        variantId: matchedVariant.id || '',
        reason: 'variant-no-image-fallback-product',
      };
    }

    if (remainder === '' || remainder === null) {
      if (productImage) {
        return {
          imageUrl: productImage,
          matchLevel: 'product',
          productHandle: productHandle,
          variantId: '',
          reason: 'product-match',
        };
      }
      return {
        imageUrl: smileImage || '',
        matchLevel: 'fallback',
        productHandle: '',
        variantId: '',
        reason: 'product-no-image',
      };
    }

    return {
      imageUrl: smileImage || '',
      matchLevel: 'fallback',
      productHandle: '',
      variantId: '',
      reason: remainder === null ? 'product-no-prefix-match' : 'variant-ambiguous',
    };
  }

  /**
   * Match the reference against a single candidate product.
   * Returns null when there is no unique match at this level.
   */
  function matchProduct(product, reference) {
    var normalizedProductTitle = normalizeReference(product.title);
    if (!normalizedProductTitle) return null;

    if (normalizedProductTitle === reference) {
      return {
        matchLevel: 'product',
        remainder: '',
        reason: 'product-exact',
      };
    }

    if (reference.length > normalizedProductTitle.length) {
      var remainder = stripProductPrefix(reference, normalizedProductTitle);
      if (remainder !== null) {
        return {
          matchLevel: 'variant',
          remainder: remainder,
          reason: 'product-prefix',
        };
      }
    }

    return null;
  }

  /**
   * Resolve a single reward presentation.
   *
   * Always resolves; never throws. On failure returns `{ matchLevel: 'fallback' }`
   * with the Smile image so the caller can render a safe card.
   */
  function resolveRewardPresentation(input, deps) {
    var smileImage = (input && input.imageUrl) || '';
    if (!deps || typeof deps.searchProducts !== 'function' || typeof deps.loadProduct !== 'function') {
      return Promise.resolve({
        imageUrl: smileImage,
        matchLevel: 'fallback',
        productHandle: '',
        variantId: '',
        reason: 'adapter-missing',
      });
    }

    var reference = pickReference(input);
    if (!reference) {
      return Promise.resolve({
        imageUrl: smileImage,
        matchLevel: 'fallback',
        productHandle: '',
        variantId: '',
        reason: 'reference-empty',
      });
    }

    var cache = deps.cache || null;
    var searchCache = (cache && cache.search) || null;
    var productCache = (cache && cache.product) || null;
    var searchPromise;
    if (searchCache && typeof searchCache.get === 'function') {
      searchPromise = searchCache.get(reference);
    }
    if (!searchPromise) {
      searchPromise = deps.searchProducts(reference, DEFAULT_LIMIT);
      if (searchCache && typeof searchCache.set === 'function') {
        searchCache.set(reference, searchPromise);
      }
    }

    // Name-slug fallback handle: built once, used by the branches below.
    // For Unlisted products that are excluded from predictive search, the
    // /products/<handle>.js endpoint can still serve the product JSON.
    var slugFallbackHandle = nameToSlug(input && input.name);
    var fallbackName = (input && input.name) || '';

    return Promise.resolve(searchPromise)
      .then(function (candidates) {
        if (!Array.isArray(candidates) || candidates.length === 0) {
          // No description search hit - try the name-slug fallback before
          // giving up.
          if (slugFallbackHandle) {
            return loadAndSelectByHandle(
              slugFallbackHandle, deps, productCache, smileImage, fallbackName
            );
          }
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: '',
            variantId: '',
            reason: 'no-candidates',
          };
        }

        var productMatches = [];
        var firstProductMatchIndex = -1;
        var variantMatches = [];

        candidates.forEach(function (candidate, index) {
          var productTitle = normalizeReference(candidate.title);
          if (!productTitle) return;
          if (productTitle === reference) {
            productMatches.push({ index: index, candidate: candidate });
            if (firstProductMatchIndex === -1) firstProductMatchIndex = index;
            return;
          }
          if (reference.length > productTitle.length) {
            var remainder = stripProductPrefix(reference, productTitle);
            if (remainder !== null) {
              variantMatches.push({ candidate: candidate, remainder: remainder });
            }
          }
        });

        if (variantMatches.length === 1) {
          return loadAndSelect(variantMatches[0].candidate, variantMatches[0].remainder, deps, productCache, smileImage);
        }

        if (variantMatches.length > 1) {
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: '',
            variantId: '',
            reason: 'multiple-variant-prefix-matches',
          };
        }

        if (productMatches.length === 1) {
          return loadAndSelect(productMatches[0].candidate, '', deps, productCache, smileImage);
        }

        // 0 or multiple product matches via the description path - try
        // the name-slug fallback before giving up.
        if (slugFallbackHandle) {
          return loadAndSelectByHandle(
            slugFallbackHandle, deps, productCache, smileImage, fallbackName
          );
        }

        return {
          imageUrl: smileImage,
          matchLevel: 'fallback',
          productHandle: '',
          variantId: '',
          reason: productMatches.length === 0 ? 'no-product-match' : 'multiple-product-matches',
        };
      })
      .catch(function () {
        // Search itself failed - try the name-slug fallback (which uses
        // a different endpoint and is not coupled to the search request).
        if (slugFallbackHandle) {
          return loadAndSelectByHandle(
            slugFallbackHandle, deps, productCache, smileImage, fallbackName
          );
        }
        return {
          imageUrl: smileImage,
          matchLevel: 'fallback',
          productHandle: '',
          variantId: '',
          reason: 'search-failed',
        };
      });
  }

  function loadAndSelect(candidate, remainder, deps, productCache, smileImage) {
    var handle = candidate.handle || '';
    var productPromise;
    if (productCache && typeof productCache.get === 'function') {
      productPromise = productCache.get(handle);
    }
    if (!productPromise) {
      productPromise = deps.loadProduct(handle);
      if (productCache && typeof productCache.set === 'function') {
        productCache.set(handle, productPromise);
      }
    }

    return Promise.resolve(productPromise)
      .then(function (product) {
        if (!product) {
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: '',
            variantId: '',
            reason: 'product-empty',
          };
        }

        // Verify the search-result title matches the loaded product title (paranoia
        // against stale cache / handle collisions).
        if (normalizeReference(product.title) !== normalizeReference(candidate.title)) {
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: '',
            variantId: '',
            reason: 'title-mismatch',
          };
        }

        return selectImage(product, remainder, smileImage);
      })
      .catch(function () {
        return {
          imageUrl: smileImage,
          matchLevel: 'fallback',
          productHandle: '',
          variantId: '',
          reason: 'product-load-failed',
        };
      });
  }

  /**
   * Load a product directly by handle (slug) and pick its featured image.
   *
   * Used as a fallback for Unlisted products that are excluded from
   * predictive search. The /products/<handle>.js endpoint still serves
   * Unlisted product JSON, so a slug direct call can recover the real
   * product image for these cases.
   *
   * `expectedName` is the reward name we expected this slug to point to.
   * We verify normalizeReference(product.title) matches
   * normalizeReference(expectedName) before accepting the image - this
   * guards against a slug collision or a handle owned by a different
   * product than the reward references.
   */
  function loadAndSelectByHandle(handle, deps, productCache, smileImage, expectedName) {
    if (!handle) {
      return Promise.resolve({
        imageUrl: smileImage,
        matchLevel: 'fallback',
        productHandle: '',
        variantId: '',
        reason: 'name-slug-empty',
      });
    }

    var productPromise;
    if (productCache && typeof productCache.get === 'function') {
      productPromise = productCache.get(handle);
    }
    if (!productPromise) {
      productPromise = deps.loadProduct(handle);
      if (productCache && typeof productCache.set === 'function') {
        productCache.set(handle, productPromise);
      }
    }

    var expectedTitle = normalizeReference(expectedName);

    return Promise.resolve(productPromise)
      .then(function (product) {
        if (!product) {
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: '',
            variantId: '',
            reason: 'name-slug-product-empty',
          };
        }

        // Paranoia: confirm the loaded product is actually what the reward name pointed to.
        if (expectedTitle && normalizeReference(product.title) !== expectedTitle) {
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: '',
            variantId: '',
            reason: 'name-slug-title-mismatch',
          };
        }

        var productImage = product.featuredImage || '';
        if (!productImage) {
          return {
            imageUrl: smileImage,
            matchLevel: 'fallback',
            productHandle: product.handle || handle,
            variantId: '',
            reason: 'name-slug-product-no-image',
          };
        }

        return {
          imageUrl: productImage,
          matchLevel: 'product',
          productHandle: product.handle || handle,
          variantId: '',
          reason: 'name-slug-match',
        };
      })
      .catch(function () {
        return {
          imageUrl: smileImage,
          matchLevel: 'fallback',
          productHandle: '',
          variantId: '',
          reason: 'name-slug-product-load-failed',
        };
      });
  }

  var api = {
    normalizeReference: normalizeReference,
    cleanRewardName: cleanRewardName,
    nameToSlug: nameToSlug,
    extractReferenceFromRewardDescription: extractReferenceFromRewardDescription,
    pickReference: pickReference,
    createShopifyCatalogAdapter: createShopifyCatalogAdapter,
    resolveRewardPresentation: resolveRewardPresentation,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    window.SmileRewardPresentation = api;
  }
})();